import React, {useRef, useState} from 'react';
import classNames from 'classnames';

import {FileInfo} from '@mattermost/types/files';
import Menu from 'components/widgets/menu/menu';
import Tooltip from 'components/tooltip';
import MenuWrapper from 'components/widgets/menu/menu_wrapper';
import OverlayTrigger from 'components/overlay_trigger';
import MoreIcon from 'components/widgets/icons/more_icon';
import GetPublicModal from 'components/get_public_link_modal';

import Constants, {ModalIdentifiers} from 'utils/constants';
import {localizeMessage} from 'utils/utils';

import type {FileDropdownPluginComponent} from 'types/store/plugins';
import type {ModalData} from 'types/actions';

type Props = {
    pluginMenuItems: FileDropdownPluginComponent[];
    fileInfo: FileInfo;
    enablePublicLink: boolean;
    handleFileDropdownOpened?: (open: boolean) => void;
    openModal: <P>(modalData: ModalData<P>) => void;
};

export const FileAttachmentMenu = (props: Props) => {
    const {
        enablePublicLink, fileInfo, pluginMenuItems, openModal,
        handleFileDropdownOpened,
    } = props;
    const [keepOpen, setKeepOpen] = useState(false);
    const [openUp, setOpenUp] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleGetPublicLink = () => {
        openModal({
            modalId: ModalIdentifiers.GET_PUBLIC_LINK_MODAL,
            dialogType: GetPublicModal,
            dialogProps: {
                fileId: fileInfo.id,
            },
        });
    };
    const setMenuPosition = () => {
        if (!buttonRef.current) {
            return;
        }

        const anchorRect = buttonRef.current?.getBoundingClientRect();
        let y;
        if (typeof anchorRect?.y === 'undefined') {
            y = typeof anchorRect?.top === 'undefined' ? 0 : anchorRect?.top;
        } else {
            y = anchorRect?.y;
        }
        const windowHeight = window.innerHeight;

        const totalSpace = windowHeight - 80;
        const spaceOnTop = y - Constants.CHANNEL_HEADER_HEIGHT;
        const spaceOnBottom = totalSpace - (spaceOnTop + Constants.POST_AREA_HEIGHT);

        setOpenUp(spaceOnTop > spaceOnBottom);
    };
    const handleDropdownOpened = (open: boolean) => {
        handleFileDropdownOpened?.(open);
        setKeepOpen(open);

        if (open) {
            setMenuPosition();
        }
    };

    let divider;
    const defaultItems = [];
    if (enablePublicLink) {
        defaultItems.push(
            <Menu.ItemAction
                data-title='Public Image'
                onClick={handleGetPublicLink}
                ariaLabel={localizeMessage('view_image_popover.publicLink', 'Get a public link')}
                text={localizeMessage('view_image_popover.publicLink', 'Get a public link')}
            />,
        );
    }

    const pluginItems = pluginMenuItems?.
        filter((item) => item?.match(fileInfo)).
        map((item) => {
            return (
                <Menu.ItemAction
                    id={item.id + '_pluginmenuitem'}
                    key={item.id + '_pluginmenuitem'}
                    onClick={() => item?.action(fileInfo)}
                    text={item.text}
                />
            );
        });

    const isMenuVisible = defaultItems?.length || pluginItems?.length;
    if (!isMenuVisible) {
        return null;
    }

    const isDividerVisible = defaultItems?.length && pluginItems?.length;
    if (isDividerVisible) {
        divider = <li id={`divider_file_${fileInfo.id}_plugins`} className='MenuItem__divider' role='menuitem' />;
    }

    const tooltip = (
        <Tooltip id='file-name__tooltip'>
            {localizeMessage('file_search_result_item.more_actions', 'More Actions')}
        </Tooltip>
    );

    return (
        <MenuWrapper onToggle={handleDropdownOpened}>
            <OverlayTrigger className='hidden-xs' delayShow={Constants.OVERLAY_TIME_DELAY_LONG} placement='top' overlay={tooltip}>
                <button
                    ref={buttonRef}
                    id={`file_action_button_${fileInfo.id}`}
                    aria-label={localizeMessage(
                        'file_search_result_item.more_actions',
                        'More Actions',
                    ).toLowerCase()}
                    className={classNames('file-dropdown-icon', 'dots-icon', {'a11y--active': keepOpen})}
                    aria-expanded={keepOpen}
                >
                    <MoreIcon />
                </button>
            </OverlayTrigger>
            <Menu
                id={`file_dropdown_${fileInfo.id}`}
                ariaLabel={'file menu'}
                openLeft={true}
                openUp={openUp}
            >
                {defaultItems}
                {divider}
                {pluginItems}
            </Menu>
        </MenuWrapper>
    );
};
